import cx from 'classnames'
import React, { useState, useEffect } from 'react'

import i18n from 'live/services/i18n'

import { IAlarp, IBarrierStatusSpecialStatus } from 'plugin-codaf-risk-ext-bowtie/types'
import { globalComponentsIndex } from 'plugin-codaf-risk-ext-bowtie/utils'

import { bowtieTheme } from '../../../../../../../utils/constants'
import { IOverviewOperation, IBarrierCount } from '../../../../AssetSelector'
import { mapJoin } from '../../AssetCard'
import { OperationBowtieItem } from './components'
import { OperationItemHeader, OperationItemBowties } from './styles'

interface IProps {
    id: string
    inactiveStatus: IBarrierStatusSpecialStatus
    activeStatus: IBarrierStatusSpecialStatus
    operation: IOverviewOperation
    operationStatusCount: Map<number, IBarrierCount>
    setSelectedBowtie: React.Dispatch<React.SetStateAction<number>>
    alarpList: IAlarp[]
}

export interface IActiveInactive {
    total: number
    active?: number
    inactive?: number
}

export const OperationItem: React.FC<IProps> = ({
    inactiveStatus,
    activeStatus,
    operation,
    setSelectedBowtie,
    operationStatusCount,
    alarpList
}) => {
    const operationHeaderGlobalIndex: number = globalComponentsIndex.incrementAndGet()
    const operationBowtieHeaderGlobalIndex: number = globalComponentsIndex.incrementAndGet()

    const [isExpanded, setIsExpanded] = useState(false)
    const [totalOperationBowtieCount, setTotalOperationBowtieCount] = useState<IActiveInactive>({
        total: 0,
        active: 0,
        inactive: 0
    })
    const [totalBowtiesWithInactiveBarriers, setTotalBowtiesWithInactiveBarriers] = useState<IActiveInactive>({
        total: 0,
        active: 0,
        inactive: 0
    })
    const [operationMitigationTotal, setOperationMitigationTotal] = useState<IActiveInactive>({
        total: 0,
        active: 0,
        inactive: 0
    })
    const [operationPreventionTotal, setOperationPreventionTotal] = useState<IActiveInactive>({
        total: 0,
        active: 0,
        inactive: 0
    })

    function getOperationTotalBarriers(): void {
        const items: IActiveInactive = { total: 0, active: 0, inactive: 0 }

        operationStatusCount.forEach(bowtieCount => {
            items.total = items.total + mapJoin(bowtieCount.prevention) + mapJoin(bowtieCount.mitigation)
            items.active =
                items.active +
                (bowtieCount.prevention.get(activeStatus.id) ?? 0) +
                (bowtieCount.mitigation.get(activeStatus.id) ?? 0)
            items.inactive =
                items.inactive +
                (bowtieCount.prevention.get(inactiveStatus.id) ?? 0) +
                (bowtieCount.mitigation.get(inactiveStatus.id) ?? 0)
            const keys = Array.from(
                new Set<number>([...bowtieCount.prevention.keys(), ...bowtieCount.mitigation.keys()])
            ).filter(e => e > 0)
            keys.forEach(key => {
                items[key] =
                    (items[key] ?? 0) + (bowtieCount.prevention.get(key) ?? 0) + (bowtieCount.mitigation.get(key) ?? 0)
            })
        })
        setTotalOperationBowtieCount(items)
    }

    function getOperationMitigationTotal(): void {
        const items: IActiveInactive = { total: 0, active: 0, inactive: 0 }

        operationStatusCount.forEach(bowtieCount => {
            items.total = items.total + mapJoin(bowtieCount.mitigation)
            items.active = items.active + (bowtieCount.mitigation.get(activeStatus.id) ?? 0)
            items.inactive = items.inactive + (bowtieCount.mitigation.get(inactiveStatus.id) ?? 0)
        })

        setOperationMitigationTotal(items)
    }

    function getOperationPreventionTotal(): void {
        const items: IActiveInactive = { total: 0, active: 0, inactive: 0 }

        operationStatusCount.forEach(bowtieCount => {
            items.total = items.total + mapJoin(bowtieCount.prevention)
            items.active = items.active + (bowtieCount.prevention.get(activeStatus.id) ?? 0)
            items.inactive = items.inactive + (bowtieCount.prevention.get(inactiveStatus.id) ?? 0)
        })

        setOperationPreventionTotal(items)
    }

    function getTotalBowtiesWithInactiveBarriers(): void {
        const items: IActiveInactive = { total: 0, active: 0, inactive: 0 }

        operationStatusCount.forEach(bowtieCount => {
            items.total = items.total + 1
            const active =
                (bowtieCount.prevention.get(activeStatus.id) ?? 0) + (bowtieCount.mitigation.get(activeStatus.id) ?? 0)
            const inactive =
                (bowtieCount.prevention.get(inactiveStatus.id) ?? 0) +
                (bowtieCount.mitigation.get(inactiveStatus.id) ?? 0)
            if (active > 0) {
                items.active = items.active + 1
            }
            if (inactive > 0) {
                items.inactive = items.inactive + 1
            }
        })
        setTotalBowtiesWithInactiveBarriers(items)
    }

    const getActualColor = (active: number, total: number): string => {
        const percent = active / total
        if (percent >= 0.85 && percent <= 1) {
            return bowtieTheme.barrierColors.active
        } else if (percent > 0.5 && percent < 0.85) {
            return bowtieTheme.barrierColors.degraded
        } else {
            return bowtieTheme.barrierColors.inactive
        }
    }

    const getActualColorForMitigationAndPrevention = (actualCount: number, total: number): string => {
        const percent = actualCount / total
        if (percent >= 0 && percent < 0.15) {
            return bowtieTheme.barrierColors.active
        } else if (percent >= 0.15 && percent <= 0.5) {
            return bowtieTheme.barrierColors.degraded
        } else {
            return bowtieTheme.barrierColors.inactive
        }
    }

    const getStatusIcon = (): JSX.Element => {
        const color = getActualColorForMitigationAndPrevention(
            totalOperationBowtieCount.total - totalOperationBowtieCount.active,
            totalOperationBowtieCount.total
        )
        const percent =
            (totalOperationBowtieCount.total - totalOperationBowtieCount.active) / totalOperationBowtieCount.total
        const iconName =
            percent >= 0 && percent < 0.15 ? 'export-pdf far fa-check-circle' : 'export-pdf far fa-exclamation-triangle'

        return <i className={iconName} style={{ color: color, marginTop: 2 }} />
    }

    useEffect(() => {
        if (operationStatusCount) {
            getOperationTotalBarriers()
            getTotalBowtiesWithInactiveBarriers()
            getOperationMitigationTotal()
            getOperationPreventionTotal()
        }
    }, [operationStatusCount]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!operationStatusCount) return <>carregando</> // TODO: trocar por loading

    return (
        <>
            <OperationItemHeader
                onClick={() => setIsExpanded(old => !old)}
                className={`export-pdf operation-header ${operationHeaderGlobalIndex % 11 === 0 ? 'divider-pdf' : ''}`}
            >
                {getStatusIcon()}
                <div className="operation-item-description">{operation.description.toUpperCase()}</div>
                <div className="export-pdf operation-item-degraded-barriers">
                    <span
                        style={{
                            color: getActualColorForMitigationAndPrevention(
                                totalOperationBowtieCount.total - totalOperationBowtieCount.active,
                                totalOperationBowtieCount.total
                            )
                        }}
                    >
                        {totalOperationBowtieCount.total - totalOperationBowtieCount.active}
                    </span>
                    /{totalOperationBowtieCount.total}
                    {i18n(` ${inactiveStatus.statusCustomName ?? inactiveStatus.name} barriers`)}
                </div>
                <div className="operation-item-expand">
                    <i className={`export-pdf fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                </div>
            </OperationItemHeader>
            <OperationItemBowties
                isExpanded={isExpanded}
                className={cx('export-pdf operation-item-bowties', {
                    'divider-pdf': operationBowtieHeaderGlobalIndex % 11 === 0
                })}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className={`export-pdf operation-bowtie-header`}>
                    <div className="export-pdf operation-bowtie-degraded-count">
                        <div className="export-pdf number-count-bowtie">
                            <span
                                className="export-pdf number-count-bowtie"
                                style={{
                                    color: getActualColor(
                                        totalBowtiesWithInactiveBarriers.total -
                                            totalBowtiesWithInactiveBarriers.inactive,
                                        totalBowtiesWithInactiveBarriers.total
                                    )
                                }}
                            >
                                {totalBowtiesWithInactiveBarriers.inactive}
                            </span>
                            /{totalBowtiesWithInactiveBarriers.total}
                        </div>
                        {i18n('BOWTIES WITH DEGRADED BARRIERS')}
                    </div>
                    <div className="operation-bowtie-barrier-resume prevention export-pdf number-count-prevention-mitigation">
                        <div className="export-pdf operation-bowtie-barrier-label">{i18n('PREVENTION')}</div>
                        <span
                            className="export-pdf number-count-bowtie"
                            style={{
                                color: getActualColorForMitigationAndPrevention(
                                    operationPreventionTotal.total - operationPreventionTotal.active,
                                    operationPreventionTotal.total
                                )
                            }}
                        >
                            {operationPreventionTotal.total - operationPreventionTotal.active}
                        </span>
                        /{operationPreventionTotal.total}
                    </div>
                    <div className="operation-bowtie-barrier-resume mitigation export-pdf number-count-prevention-mitigation">
                        <div className="export-pdf operation-bowtie-barrier-label">{i18n('MITIGATION')}</div>
                        <span
                            className="export-pdf number-count-bowtie"
                            style={{
                                color: getActualColorForMitigationAndPrevention(
                                    operationMitigationTotal.total - operationMitigationTotal.active,
                                    operationMitigationTotal.total
                                )
                            }}
                        >
                            {operationMitigationTotal.total - operationMitigationTotal.active}
                        </span>
                        /{operationMitigationTotal.total}
                    </div>
                </div>
                {operation.bowtieViews
                    .sort((a, b) => {
                        if (a.bowtie.bowtieCode < b.bowtie.bowtieCode) {
                            return -1
                        }

                        if (a.bowtie.bowtieCode > b.bowtie.bowtieCode) {
                            return 1
                        }

                        return 0
                    })
                    .map((bowtieView, idx) => (
                        <OperationBowtieItem
                            key={`${idx}-${bowtieView.bowtie.id}`}
                            bowtieView={bowtieView}
                            onSelectBowtie={() => setSelectedBowtie(bowtieView.bowtie.id)}
                            bowtieStatusCount={operationStatusCount.get(bowtieView.bowtie.id)}
                            alarpList={alarpList}
                            activeStatus={activeStatus}
                            inactiveStatus={inactiveStatus}
                        />
                    ))}
            </OperationItemBowties>
        </>
    )
}
